import { useRef, useState } from "react";
import { markdownComponents } from "@/ui/CommunicationView/ChatConversation/PromptMessageContainer/markdown";

/**
 * Agents markdown renderers (AMB-353 / F3) = the shared platform set + a
 * copy-to-clipboard button on fenced code blocks. Scoped to the Agents screens
 * (Digest / TranscriptView / Reviews) so the platform-wide markdown renderer is
 * unchanged. react-markdown routes a fenced ``` block through the `pre` component,
 * so overriding `pre` wraps the block with a hover-revealed Copy button. Inline
 * `code` (no `pre`) is untouched.
 */

function CodeBlock({ node, children, ...props }: any) {
  void node;
  const ref = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const text = ref.current?.innerText ?? "";
    if (!text) return;
    navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={copy}
        aria-label="Copy code"
        className="absolute right-2 top-2 z-10 rounded-md bg-white/90 px-2 py-1 text-[11px] font-medium text-gray-600 opacity-0 shadow-sm ring-1 ring-black/[0.06] transition-all duration-150 hover:bg-white hover:text-gray-900 group-hover:opacity-100"
      >
        {copied ? "Copied!" : "Copy"}
      </button>
      <pre ref={ref} {...props}>
        {children}
      </pre>
    </div>
  );
}

// Render generation's <asset type label>…</asset> wrapper as a subtly-framed block with its
// label (E1/E2) — so the asset reads as the editable deliverable, and the tag never shows
// literally. rehype-raw surfaces the element + its attributes here.
function AssetBlock({ node, children }: any) {
  const label = node?.properties?.label;
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-indigo-200/60 bg-white">
      {label ? (
        <div className="border-b border-black/[0.04] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-500">{label}</div>
      ) : null}
      <div className="px-3 py-2">{children}</div>
    </div>
  );
}

// Wide tables used to bleed out of the paper container; wrap each table so it scrolls
// horizontally within its bounds instead (D-9 table-bleed fix — applies everywhere the
// agents markdown renderer is used: KB, TranscriptView, the drawers).
function ScrollableTable({ children }: any) {
  return (
    <div className="my-3 max-w-full overflow-x-auto">
      <table>{children}</table>
    </div>
  );
}

export const agentsMarkdownComponents = {
  ...markdownComponents,
  pre: CodeBlock,
  asset: AssetBlock,
  table: ScrollableTable,
};
